import React, { useCallback, useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "react-toastify";
import { describeApiError } from "../../../api/client";
import { answerOpsQuestion, listOpsQuestions } from "../../../api/opsDocsApi";
import { getUserRole } from "../../../helpers/tenantHelpers";
import "./documents.css";

/* The business questions the starter documents need answered ("Is a lifeguard ever on duty?").
   One answer fills every document that uses it AND the business's knowledge — what the
   assistant, the AI receptionist and quotes read. Shown on Operations → Documents, Knowledge
   and SOPs; same queue everywhere.

   props: keys — only these questions (a document's own); compact — a one-line summary that
          expands; onAnswered(question) — after a save; openOnly — hide answered ones. */
const AUTHOR_ROLES = new Set(["owner", "admin", "manager"]);
const human = (v) => String(v || "").replace(/_/g, " ").replace(/^\w/, (c) => c.toUpperCase());

function AnswerForm({ q, onSaved, canAnswer }) {
  const v0 = q.answer_value || {};
  const [v, setV] = useState({ yes: v0.yes ?? null, choice: v0.choice || "", amount: v0.amount ?? "", number: v0.number ?? "",
    text: v0.text || (q.answer_value ? "" : "") });
  const [busy, setBusy] = useState(false);
  const t = q.answer_type || "text";
  const save = async () => {
    const value = {};
    if (t.startsWith("boolean") && v.yes !== null) value.yes = v.yes;
    if (t.startsWith("choice") && v.choice) value.choice = v.choice;
    if (t.includes("money") && v.amount !== "") value.amount = Number(v.amount);
    if ((t === "number" || t === "days") && v.number !== "") value.number = Number(v.number);
    if (v.text.trim()) value.text = v.text.trim();
    if (!Object.keys(value).length) { toast.error("Add an answer first"); return; }
    setBusy(true);
    try {
      const r = await answerOpsQuestion(q.key, value);
      toast.success(r.data.documents_updated ? `Saved. ${r.data.documents_updated} document${r.data.documents_updated === 1 ? "" : "s"} updated.` : "Saved");
      onSaved && onSaved(r.data.question);
    } catch (e) {
      toast.error(describeApiError(e, "Couldn't save the answer"));
    } finally {
      setBusy(false);
    }
  };
  if (!canAnswer) return <div className="bkdoc-meta">An owner or manager answers this.</div>;
  return (
    <div className="qq-form">
      {t.startsWith("boolean") && (
        <div className="qq-choices" role="radiogroup" aria-label="Yes or no">
          {[[true, "Yes"], [false, "No"]].map(([val, label]) => (
            <label key={label} className={`qq-chip ${v.yes === val ? "is-on" : ""}`}>
              <input type="radio" name={`yn-${q.key}`} checked={v.yes === val} onChange={() => setV({ ...v, yes: val })} />{label}
            </label>
          ))}
        </div>
      )}
      {t.startsWith("choice") && (
        <div className="qq-choices" role="radiogroup" aria-label="Choose one">
          {(q.options || []).map((o) => (
            <label key={o} className={`qq-chip ${v.choice === o ? "is-on" : ""}`}>
              <input type="radio" name={`ch-${q.key}`} checked={v.choice === o} onChange={() => setV({ ...v, choice: o })} />{human(o)}
            </label>
          ))}
        </div>
      )}
      {t.includes("money") && (
        <input className="qq-input qq-short" type="number" min="0" step="0.01" placeholder="Amount ($)" value={v.amount}
          onChange={(e) => setV({ ...v, amount: e.target.value })} />
      )}
      {(t === "number" || t === "days") && (
        <input className="qq-input qq-short" type="number" min="0" placeholder={t === "days" ? "Days" : "Number"} value={v.number}
          onChange={(e) => setV({ ...v, number: e.target.value })} />
      )}
      {t !== "number" && t !== "days" && t !== "choice" && (
        <textarea className="qq-input" rows={t === "table" ? 4 : 2}
          placeholder={t === "table" ? "One line per case, e.g. More than 60 days before: deposit refunded" : "In your words, as it should read to a client"}
          value={v.text} onChange={(e) => setV({ ...v, text: e.target.value })} />
      )}
      <div><button className="bkdoc-btn" disabled={busy} onClick={save}>{busy ? "Saving…" : q.answered ? "Update answer" : "Save answer"}</button></div>
    </div>
  );
}

export default function QuestionQueue({ keys, compact = false, openOnly = false, onAnswered }) {
  const role = String(getUserRole("Member") || "").toLowerCase();
  const canAnswer = AUTHOR_ROLES.has(role);
  const [rows, setRows] = useState(null);
  const [open, setOpen] = useState(!compact);
  const [editing, setEditing] = useState(null);
  const keysKey = (keys || []).join(",");

  const load = useCallback(async () => {
    try {
      const r = await listOpsQuestions(false);
      let qs = r.data.questions || [];
      if (keys) qs = qs.filter((q) => keys.includes(q.key));
      setRows(qs);
    } catch {
      setRows([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [keysKey]);
  useEffect(() => { load(); }, [load]);

  if (!rows || rows.length === 0) return null;
  const pending = rows.filter((q) => !q.answered);
  const shown = (openOnly ? pending : rows).slice().sort((a, b) => Number(a.answered) - Number(b.answered));

  if (compact && !open) {
    if (!pending.length) return null;
    return (
      <div className="qq-compact">
        <span><b>{pending.length}</b> question{pending.length === 1 ? "" : "s"} about how you run things {pending.length === 1 ? "is" : "are"} waiting. Each answer fills your documents and what the assistant knows.</span>
        <button className="bkdoc-btn" onClick={() => setOpen(true)}>Answer</button>
        <Link className="bkdoc-btn" to="/operations/documents">Documents</Link>
      </div>
    );
  }

  return (
    <div className="qq">
      {!keys && <div className="qq-head"><b>Questions</b> <span className="bkdoc-meta">{pending.length} waiting · {rows.length - pending.length} answered. An answer fills every document that uses it and what the assistant knows.</span></div>}
      {shown.map((q) => (
        <div className={`qq-row ${q.answered ? "is-answered" : ""}`} key={q.key}>
          <div className="qq-q">
            <div className="qq-text">{q.question}</div>
            <div className="bkdoc-meta">
              {q.needs_counsel ? <span className="qq-counsel">Counsel should check this answer</span> : null}
              {q.used_by?.length ? <span> Used in: {q.used_by.map((d) => d.title).join(", ")}</span> : null}
            </div>
            {q.why ? <div className="bkdoc-meta qq-why">{q.why}</div> : null}
          </div>
          {q.answered && editing !== q.key ? (
            <div className="qq-answer">
              <span>{q.answer_text}</span>
              {canAnswer ? <button className="bkdoc-btn" onClick={() => setEditing(q.key)}>Change</button> : null}
            </div>
          ) : (
            <AnswerForm q={q} canAnswer={canAnswer} onSaved={(nq) => { setEditing(null); load(); onAnswered && onAnswered(nq); }} />
          )}
        </div>
      ))}
    </div>
  );
}
